import { FC, ReactElement, useContext } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { faSearch, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AddToProjectModalContext,
  ComponentSpinner,
  EmptyRecordsMessage,
  Icon,
  MoreToolsDropdown,
  Pagination,
  RECORD_TOOLS,
  ResultWithSortAndPagination,
  SavedInstanceToolButton,
  TimestampedRecordWithData,
} from '@app/components';
import { useInstanceDetailsRoutePath } from '@app/hooks';
import { getItemsPerPage } from '@app/utils';
import { resultCountMessages, sharedMessages } from '@app/translations';
import { Route } from '@app/constants';

import { useDashboardListSorting } from '../../hooks/useDashboardListSorting';
import { usePaginatedItemsPerPage } from '../../hooks/usePaginatedItemsPerPage';
import { DashboardRecordCard } from '../DashboardRecordCard';

import '../HistoryRecordsPageWrapper.scss';

export type onRecordRemoval = (record: TimestampedRecordWithData) => void;

interface DashboardInstancesListProps {
  list: TimestampedRecordWithData[];
  emptyStateMessage: string;
  emptyStateIcon: IconDefinition;
  isLoading?: boolean;
  actionButton?: ReactElement;
  isItemRemovable?: boolean;
  onEntryRemoval: onRecordRemoval;
}

const cnBem = createBemBlockBuilder(['historyRecordsPageWrapper']);

export const DashboardInstancesList: FC<DashboardInstancesListProps> = ({
  list = [],
  emptyStateMessage,
  emptyStateIcon,
  isLoading,
  actionButton,
  isItemRemovable,
  onEntryRemoval,
}) => {
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const { sortDirection, sortOptions, getSortedList, handleSortChange } =
    useDashboardListSorting();
  const {
    pageNumber,
    recordsPerPage,
    handlePageChange,
    handleTitlePerPageUpdate,
  } = usePaginatedItemsPerPage();
  const { $t } = useIntl();
  const { dispatch: updateAddToProjectModalState } = useContext(
    AddToProjectModalContext
  );

  const getRecordTools = (record: TimestampedRecordWithData) => {
    const isNonExistingRecord = isEmpty(record.title);

    if (isNonExistingRecord) {
      return (
        <MoreToolsDropdown
          tools={[
            {
              ...RECORD_TOOLS.delete,
              onClick: () => onEntryRemoval(record),
            },
          ]}
        />
      );
    }

    return (
      <>
        <SavedInstanceToolButton
          entry={record}
          idKey="instanceId"
          isItemRemovable={isItemRemovable}
        />
        <MoreToolsDropdown
          tools={[
            {
              ...RECORD_TOOLS.addToProject,
              onClick: () =>
                updateAddToProjectModalState({
                  type: 'OPEN_MODAL',
                  payload: {
                    id: record.instanceId,
                    redirectBackRoute: `${generatePath(Route.instanceDetails, {
                      id: record.instanceId,
                    })}?isAddToProject=true`,
                  },
                }),
            },
            {
              ...RECORD_TOOLS.delete,
              onClick: () => onEntryRemoval(record),
            },
          ]}
        />
      </>
    );
  };

  const renderCurrentState = () => {
    if (isLoading) {
      return <ComponentSpinner />;
    }

    if (isEmpty(list)) {
      return (
        <EmptyRecordsMessage
          className={cnBem('__emptyRecordsMessage')}
          icon={emptyStateIcon}
          message={emptyStateMessage}
        >
          <Link to={Route.search}>
            <Icon icon={faSearch}>{$t(sharedMessages.startANewSearch)}</Icon>
          </Link>
        </EmptyRecordsMessage>
      );
    }

    return (
      <>
        <ResultWithSortAndPagination
          recordsCount={list.length}
          recordsPerPage={recordsPerPage}
          pageNumber={pageNumber}
          countMessage={resultCountMessages.numberOfRecords}
          sortField="name"
          sortDirection={sortDirection}
          sortOptions={sortOptions}
          onSortChange={handleSortChange}
          onRecordsPerPageChange={handleTitlePerPageUpdate}
        />
        {getItemsPerPage(getSortedList(list), recordsPerPage, pageNumber).map(
          record => (
            <DashboardRecordCard
              title={record.title}
              titleLink={getInstanceDetailsRoutePath(
                record.instanceId || record.id
              )}
              subTitle={record.sourceTypes?.join(', ')}
              contributors={record.contributors}
              timestamp={record.timestamp}
              publication={record.publication}
              key={record.id + record.timestamp}
              tools={getRecordTools(record)}
              {...(!isEmpty(record.title) && { actionButton })}
            />
          )
        )}
        <Pagination
          pageNumber={pageNumber}
          totalRecords={list.length}
          recordsPerPage={recordsPerPage}
          onPageChange={data => {
            handlePageChange(data);
          }}
        />
      </>
    );
  };

  return <div data-auto="instances-list">{renderCurrentState()}</div>;
};
